import type { RouteRecordRaw } from "vue-router";

import { getRouteParam, getRouteParamOrEmpty } from "../utils";

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: "/cameras-overview",
    name: "cameras-overview",
    component: () =>
      import(/* webpackChunkName: "dashboard-cameras-overview" */ "../../components/cameras/CamerasOverviewView.vue"),
    meta: { titleKey: "pageTitle.camerasOverview", viewGroup: "cameras-overview" }
  },
  {
    path: "/applications",
    name: "applications",
    component: () =>
      import(/* webpackChunkName: "dashboard-applications" */ "../../components/applications/ApplicationsView.vue"),
    meta: { titleKey: "pageTitle.applications", viewGroup: "applications" }
  },
  {
    path: "/users",
    name: "users",
    component: () => import(/* webpackChunkName: "dashboard-users" */ "../../components/users/UsersView.vue"),
    meta: { titleKey: "pageTitle.users", viewGroup: "users" }
  },
  {
    path: "/users/new",
    name: "user-create",
    component: () => import(/* webpackChunkName: "dashboard-users" */ "../../components/users/UserDetailView.vue"),
    meta: { titleKey: "pageTitle.userCreate", viewGroup: "user-create" }
  },
  {
    path: "/users/:id",
    name: "user-edit",
    component: () => import(/* webpackChunkName: "dashboard-users" */ "../../components/users/UserDetailView.vue"),
    props: route => ({
      id: getRouteParamOrEmpty(route, "id")
    }),
    meta: { titleKey: "pageTitle.userEdit", viewGroup: "user-edit", viewGroupParams: ["id"] }
  },
  {
    path: "/organizations",
    name: "organizations",
    component: () =>
      import(/* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationsView.vue"),
    meta: { titleKey: "pageTitle.organizations", viewGroup: "organizations" }
  },
  {
    path: "/organizations/new",
    name: "organizations-create",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationCreateView.vue"
      ),
    meta: { titleKey: "pageTitle.organizationsCreate", viewGroup: "organizations-create" }
  },
  {
    path: "/organizations/:id",
    name: "organizations-details",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationDetailView.vue"
      ),
    props: route => ({
      id: getRouteParamOrEmpty(route, "id"),
      section: "details" as const
    }),
    meta: { titleKey: "pageTitle.organizationsDetails", viewGroup: "organizations-details", viewGroupParams: ["id"] }
  },
  {
    path: "/organizations/:id/applications",
    name: "organizations-details-applications",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationDetailView.vue"
      ),
    props: route => ({
      id: getRouteParamOrEmpty(route, "id"),
      section: "applications" as const
    }),
    meta: { titleKey: "pageTitle.organizationsDetails", viewGroup: "organizations-details", viewGroupParams: ["id"] }
  },
  {
    path: "/organizations/:id/users",
    name: "organizations-details-users",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationDetailView.vue"
      ),
    props: route => ({
      id: getRouteParamOrEmpty(route, "id"),
      section: "users" as const
    }),
    meta: { titleKey: "pageTitle.organizationsDetails", viewGroup: "organizations-details", viewGroupParams: ["id"] }
  },
  {
    path: "/organizations/:id/subs",
    name: "organizations-details-subs",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-organizations" */ "../../components/organizations/OrganizationDetailView.vue"
      ),
    props: route => ({
      id: getRouteParamOrEmpty(route, "id"),
      section: "subs" as const
    }),
    meta: { titleKey: "pageTitle.organizationsDetails", viewGroup: "organizations-details", viewGroupParams: ["id"] }
  },
  {
    path: "/manual-approval",
    name: "manual-approval",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-manual-approval" */ "../../components/manual-approval/ManualApprovalView.vue"
      ),
    meta: { titleKey: "pageTitle.manualApproval", viewGroup: "manual-approval" }
  },
  {
    path: "/manual-approval-review",
    name: "manual-approval-review",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-manual-approval-review" */ "../../components/manual-approval-review/ManualApprovalReviewView.vue"
      ),
    meta: { titleKey: "pageTitle.manualApprovalReview", viewGroup: "manual-approval-review" }
  },
  {
    path: "/traffic-control",
    name: "traffic-control",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-traffic-control" */ "../../components/traffic-control/TrafficControlView.vue"
      ),
    meta: { titleKey: "pageTitle.trafficControl", viewGroup: "traffic-control" }
  },
  {
    path: "/system",
    name: "system",
    component: () => import("../../components/system/SystemView.vue"),
    meta: { titleKey: "pageTitle.system", viewGroup: "system" }
  },
  {
    path: "/audits",
    name: "audits",
    component: () => import(/* webpackChunkName: "dashboard-audits" */ "../../components/audits/AuditsView.vue"),
    meta: { titleKey: "pageTitle.audits", viewGroup: "audits" }
  },
  {
    path: "/statistics/:dashboard?",
    name: "statistics",
    component: () =>
      import(/* webpackChunkName: "dashboard-statistics" */ "../../components/statistics/StatisticsView.vue"),
    props: route => ({
      dashboard: getRouteParam(route, "dashboard")
    }),
    meta: { titleKey: "pageTitle.statistics", viewGroup: "statistics", viewGroupParams: ["dashboard"] }
  },
  {
    path: "/telemetry",
    name: "telemetry",
    component: () =>
      import(/* webpackChunkName: "dashboard-telemetry" */ "../../components/telemetry/TelemetryView.vue"),
    meta: { titleKey: "pageTitle.telemetry", viewGroup: "telemetry" }
  },
  {
    path: "/telemetry/annotations",
    name: "telemetry-annotations",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-telemetry-annotations" */ "../../components/telemetry/TelemetryAnnotationsView.vue"
      ),
    meta: { titleKey: "pageTitle.telemetryAnnotations", viewGroup: "telemetry-annotations" }
  },
  {
    path: "/vehicle-registry",
    name: "vehicle-registry",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-vehicle-registry" */ "../../components/vehicle-registry/VehicleRegistryView.vue"
      ),
    meta: { titleKey: "pageTitle.vehicleRegistry", viewGroup: "vehicle-registry" }
  }
];
